import { z } from 'zod';
import { holdConfigSchema } from './pricing';
import { paymentModeSchema } from './reservation';

// Cents, not euros: money never travels as a float. A zone may also be free.
const priceCents = z.number().int().min(0).max(10_000_000);

// Hours before arrival. Full outside the first, nothing inside the last. §13
export const cancellationPolicySchema = z
  .object({
    fullHours: z.number().int().min(0).max(720),
    partialPct: z.number().int().min(0).max(100),
    noneHours: z.number().int().min(0).max(720),
  })
  // Both ends of the pair are named, so the screen can mark both. §15
  .superRefine((policy, ctx) => {
    if (policy.fullHours >= policy.noneHours) return;
    for (const path of ['fullHours', 'noneHours'] as const)
      ctx.addIssue({
        code: 'custom',
        path: [path],
        message: 'the no-refund window cannot open before the full-refund one closes',
      });
  });

export type CancellationPolicy = z.infer<typeof cancellationPolicySchema>;

// An inverted window offers no deadline at all, on any day: §9 refuses it here.
export const holdSettingSchema = holdConfigSchema.superRefine((hold, ctx) => {
  if (hold.includedHour <= hold.latestHour) return;
  for (const path of ['includedHour', 'latestHour'] as const)
    ctx.addIssue({
      code: 'custom',
      path: [path],
      message: 'the latest hold hour cannot come before the free one',
    });
});

// A key is the venue's own handle for a zone; names carry it per language. §6
export const zoneSettingSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'must be lowercase letters, digits and single hyphens'),
  names: z.record(z.string(), z.string().trim().min(1).max(60)),
  dayPriceCents: priceCents,
  holdPerHourCents: priceCents,
});

export type ZoneSetting = z.infer<typeof zoneSettingSchema>;

export const MAX_ZONES = 40;

export const venueSettingsUpdateSchema = z.object({
  hold: holdSettingSchema,
  paymentMode: paymentModeSchema,
  cancellation: cancellationPolicySchema,
  zones: z
    .array(zoneSettingSchema)
    .max(MAX_ZONES)
    .refine((zones) => new Set(zones.map((zone) => zone.key)).size === zones.length, {
      message: 'two zones cannot share a key',
    }),
});

export type VenueSettingsUpdate = z.infer<typeof venueSettingsUpdateSchema>;

// What settings summarises; the map is the only editor of the staff-only flag. §11
export const venueSummarySchema = z.object({
  spots: z.number().int().nonnegative(),
  staffOnly: z.number().int().nonnegative(),
  gridRows: z.number().int().nonnegative(),
  gridCols: z.number().int().nonnegative(),
  numberFrom: z.number().int().positive().nullable(),
  numberTo: z.number().int().positive().nullable(),
});

export type VenueSummary = z.infer<typeof venueSummarySchema>;

export const venueSettingsSchema = z.object({
  hold: holdSettingSchema,
  paymentMode: paymentModeSchema,
  cancellation: cancellationPolicySchema,
  zones: z.array(zoneSettingSchema),
  currency: z.string(),
  languages: z.array(z.string()).min(1),
  defaultLanguage: z.string(),
  spotKind: z.string(),
  summary: venueSummarySchema,
});

export type VenueSettings = z.infer<typeof venueSettingsSchema>;

// A zone the venue offers a language for but has not named yet is not saveable.
export function unnamedLanguagesOf(
  zone: ZoneSetting,
  languages: readonly string[],
): readonly string[] {
  return languages.filter((language) => (zone.names[language] ?? '').trim() === '');
}

// Which controls a refused save named, so the screen can point at them. §15
export function settingsProblems(
  update: VenueSettingsUpdate,
  languages: readonly string[],
): readonly string[] {
  const problems = new Set<string>();
  const parsed = venueSettingsUpdateSchema.safeParse(update);
  if (!parsed.success) for (const issue of parsed.error.issues) problems.add(issue.path.join('.'));
  for (const [index, zone] of update.zones.entries())
    for (const language of unnamedLanguagesOf(zone, languages))
      problems.add(`zones.${index}.names.${language}`);
  return [...problems];
}
